import React, { useState, useEffect, useContext, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Pagination, Col, Button, Form } from 'react-bootstrap';
import axios from 'axios';
import { BsFillGrid1X2Fill } from 'react-icons/bs';
import { FaThList } from 'react-icons/fa';
import { authContext } from '../Context/AuthContext';
import { globalContext } from '../Context/GlobalContext';
import TablesCategorias from './TablesCategorias';
import CardCategorias from './CardCategorias';
import { useTranslation } from 'react-i18next';
import { MdDownload, MdRefresh } from "react-icons/md";
import { generarExcel } from '../../utilities/helpers/generadorExcel';
import PropTypes from 'prop-types';
import "./ApiCategoriasStyle.css"

const apiProductos = process.env.REACT_APP_API_PRODUCTOS;

// Componente ErrorBoundary mejorado
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("ErrorBoundary caught an error", error, errorInfo);
        this.setState({ errorInfo });
        
        if (error.message.includes('localStorage')) {
            localStorage.removeItem('artXCategoria');
        }
    }

    handleReload = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="alert alert-danger text-center p-4">
                    <h4 className="mb-3">Algo salió mal</h4>
                    <p className="mb-3">{this.state.error?.message || 'Error desconocido'}</p>
                    <button className="btn btn-primary" onClick={this.handleReload}>
                        Recargar página
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

// Componente de carga mejorado
const EnhancedSpinner = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
        </div>
    </div>
);

// Validación de props
CardCategorias.propTypes = {
    articulosPorCategoria: PropTypes.array.isRequired
};

TablesCategorias.propTypes = {
    articulosPorCategoria: PropTypes.array.isRequired,
    header: PropTypes.array.isRequired
};

export const ApiCategorias = () => {
    const { codigo } = useParams();
    const [articulosPorCategoria, setArticulosPorCategoria] = useState([]);
    const [load, setLoad] = useState(false);
    const { handleDisplayGrid, handleDisplayList, grid } = useContext(globalContext);
    const [isMobile, setIsMobile] = useState(false);
    const [largoTotal, setLargoTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const { token } = useContext(authContext);
    const elementosPorPagina = 20;
    const navigate = useNavigate();
    const [t] = useTranslation("global");
    const [showOnlyStock, setShowOnlyStock] = useState(!!token);
    const country = process.env.REACT_APP_COUNTRY;
    const [descargado, setDescargado] = useState(false);
    const [error, setError] = useState(null);
    const [filtroMarca, setFiltroMarca] = useState('');
    const [marcasDisponibles, setMarcasDisponibles] = useState([]);
    const [hayActualizacion, setHayActualizacion] = useState(false);
    const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
    const [estaActualizando, setEstaActualizando] = useState(false);
  // Función para actualización manual
  const handleActualizarManual = () => {
    obtenerDatos();
};
    // Función para obtener datos con paginación y filtros del backend
    const obtenerDatos = async () => {
        try {
            setEstaActualizando(true);
            setError(null);

            const config = {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                timeout: 10000
            };

            const params = new URLSearchParams({
                limit: '1000',
                offset: '0'
            });

            const response = await axios.get(
                `${apiProductos}/info/categoria/${codigo}?${params}`,
                config
            );

            if (!response?.data?.articulos || response.data.articulos.length === 0) {
                setArticulosPorCategoria([]);
                setLargoTotal(0);
                setMarcasDisponibles([]);
                setLoad(true);
                return;
            }

            const todosLosArticulos = response.data.articulos;

            // Obtener todas las marcas disponibles o solo las que tienen stock según el estado
            const marcasAMostrar = showOnlyStock 
                ? [...new Set(todosLosArticulos.filter(art => art.Stock > 0).map(art => art.Marca))]
                : [...new Set(todosLosArticulos.map(art => art.Marca))];
            
            setMarcasDisponibles(marcasAMostrar.filter(Boolean));

            // Aplicar filtros
            let articulosFiltrados = todosLosArticulos;
            
            if (filtroMarca) {
                articulosFiltrados = articulosFiltrados.filter(art => art.Marca === filtroMarca);
            }

            if (showOnlyStock) {
                articulosFiltrados = articulosFiltrados.filter(art => art.Stock > 0);
            }

            // Actualizar total y aplicar paginación
            setLargoTotal(articulosFiltrados.length);

            const inicio = (currentPage - 1) * elementosPorPagina;
            const fin = inicio + elementosPorPagina;
            const articulosPaginados = articulosFiltrados.slice(inicio, fin);

            setArticulosPorCategoria(articulosPaginados);
            setLoad(true);
            setUltimaActualizacion(Date.now());

            // Actualizar indicador de actualización si es necesario
            if (ultimaActualizacion) {
                setHayActualizacion(true);
                setTimeout(() => setHayActualizacion(false), 5000);
            }

            // Guardar en localStorage
            try {
                localStorage.setItem('artXCategoria', JSON.stringify(articulosPaginados));
            } catch (e) {
                console.error('Error al guardar en localStorage:', e);
            }

        } catch (error) {
            console.error('Error al obtener las categorías:', error);
            setError(error.message || 'Error al cargar los datos');
            setArticulosPorCategoria([]);
            setMarcasDisponibles([]);
        } finally {
            setEstaActualizando(false);
        }
    };

    // Efecto para obtener datos y actualizaciones
    useEffect(() => {
        obtenerDatos();
        const intervalo = setInterval(() => {
            if (!ultimaActualizacion || (Date.now() - ultimaActualizacion) >= 300000) {
                obtenerDatos();
            }
        }, 60000);

        return () => clearInterval(intervalo);
    }, [codigo, currentPage, showOnlyStock, filtroMarca]);

    // Resetear página y filtros al cambiar categoría
    useEffect(() => {
        setCurrentPage(1);
        setFiltroMarca('');
    }, [codigo]);

    // Detección de dispositivo móvil
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 670);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Función para renderizar la paginación
    const renderPagination = () => {
        const totalPages = Math.ceil(largoTotal / elementosPorPagina);
        const paginas = [];
        const paginasMostradas = 5;
        let inicio = Math.max(1, currentPage - Math.floor(paginasMostradas / 2));
        const fin = Math.min(totalPages, inicio + paginasMostradas - 1);

        inicio = Math.max(1, fin - paginasMostradas + 1);

        if (inicio > 1) {
            paginas.push(
                <Pagination.First key="first" onClick={() => setCurrentPage(1)} />
            );
            if (inicio > 2) {
                paginas.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        for (let i = inicio; i <= fin; i++) {
            paginas.push(
                <Pagination.Item 
                    key={i} 
                    active={i === currentPage}
                    onClick={() => setCurrentPage(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (fin < totalPages) {
            if (fin < totalPages - 1) {
                paginas.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            paginas.push(
                <Pagination.Last 
                    key="last" 
                    onClick={() => setCurrentPage(totalPages)} 
                />
            );
        }

        return paginas;
    };

    // Resto del JSX permanece igual, pero usando articulosPorCategoria directamente
    return (
        <ErrorBoundary>
            <div className="api-map-container">
                {/* <img 
                    src="https://raw.githubusercontent.com/RamiroSB/imagenes/main/rma-banner.png"
                    alt="Imagen marcas" 
                    className='imagenesBody' 
                    loading='lazy'
                    onError={(e) => e.target.style.display = 'none'}
                /> */}
                
                <Suspense fallback={<EnhancedSpinner />}>
                    {!load ? (
                        <EnhancedSpinner />
                    ) : articulosPorCategoria.length > 0 ? (
                        <Container fluid className='py-5 contenedor-categorias'>
                            {/* Vista Grid/Lista */}
                            {!isMobile && (
                                <div className="display-select-container">
                                    <Col className="d-flex justify-content-end">
                                        <div className="row">
                                            <div className="span6" style={{ width: "115px" }}>
                                                <div className="mycontent-left text-center">
                                                    <h6 className="text-uppercase">{t("producto.ver-como")}</h6>
                                                    <div className="d-flex flex-col justify-content-around gap-1">
                                                        <div
                                                            className={`d-flex flex-row align-items-center svgGrilla ${!grid ? 'activeView' : ''}`}
                                                            onClick={handleDisplayList}>
                                                            <FaThList size={"1.5em"} />
                                                        </div>
                                                        <div
                                                            className={`d-flex flex-row align-items-center svgGrilla ${grid ? 'activeView' : ''}`}
                                                            onClick={handleDisplayGrid}>
                                                            <BsFillGrid1X2Fill size={"1.5em"} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </div>
                            )}

                            

                            {/* Contenido Principal */}
                            <div style={{ display: !isMobile && 'flex', justifyContent: 'space-between' }}>
                                {/* Controles de Stock y Descarga */}
                                {(country === "USA" || (country === "ARG" && token)) && (
                                    <div className="display-select-container">
                                        <h2 className='texto-filtros-titulo'>Filtrar:</h2>
                                        <h3 className="mb-3 textos-filtros">Por stock</h3>
                                        <Form.Check
                                            type="checkbox"
                                            id="stockCheckbox"
                                            label="Solo productos con stock"
                                            checked={showOnlyStock}
                                            onChange={(e) => setShowOnlyStock(e.target.checked)}
                                            style={{
                                                fontWeight: 'bold',
                                                fontSize: '15px',
                                                color: 'black', // aquí va el color negro
                                                cursor: 'pointer',
                                                userSelect: 'none'
                                            }}
                                        />

                                            {isMobile ? (

                                            <div className="mb-4">
                                            <Row className="justify-content-center">
                                                <Col md={8} className="text-center">
                                                    <h4 className="mb-3">Filtrar por Marca</h4>
                                                    <div className="d-flex justify-content-center gap-3 align-items-center">
                                                        <Form.Select
                                                            style={{ width: '300px' }}
                                                            value={filtroMarca}
                                                            onChange={(e) => {
                                                                setFiltroMarca(e.target.value);
                                                                setCurrentPage(1);
                                                            }}
                                                        >
                                                            <option value="">Todas las marcas</option>
                                                            {marcasDisponibles.map((marca, index) => (
                                                                <option key={index} value={marca}>
                                                                    {marca}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                        {filtroMarca && (
                                                            <Button 
                                                                variant="outline-secondary"
                                                                onClick={() => {
                                                                    setFiltroMarca('');
                                                                    setCurrentPage(1);
                                                                }}
                                                            >
                                                                Limpiar filtro
                                                            </Button>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>
                                            </div>
                                                ) : (
                                                    <div className="mb-4">
                                            <                               h3 className="mb-3 mt-3 textos-filtros">Por Marca</h3>
                                                        <Col md={8} className="text-center">
                                                            <div style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'flex-start',
                                                                gap: '8px',
                                                            }}>
                                                                {/* Checkbox: Todas las marcas */}
                                                                <label style={{
                                                                    display: 'flex',
                                                                    fontSize: '15px',
                                                                    alignItems: 'center',
                                                                    gap: '10px',
                                                                    cursor: 'pointer',
                                                                    fontWeight: filtroMarca === '' ? 'bold' : 'normal',
                                                                    color: 'black',
                                                                    whiteSpace: 'nowrap',         // evita salto de línea
                                                                    overflowX: 'auto',             // permite scroll si el texto es MUY largo
                                                                    maxWidth: '180%'           // asegura que respete el contenedor
                                                                }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={filtroMarca === ''}
                                                                        onChange={() => {
                                                                            setFiltroMarca('');
                                                                            setCurrentPage(1);
                                                                        }}
                                                                    />
                                                                    Todas las marcas
                                                                </label>

                                                                {/* Marcas */}
                                                                {marcasDisponibles.map((marca, index) => (
                                                                    <label key={index} style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        fontSize: '15px',   
                                                                        gap: '10px',
                                                                        cursor: 'pointer',
                                                                        fontWeight: filtroMarca === marca ? 'bold' : 'normal',
                                                                        color: 'black',
                                                                        whiteSpace: 'nowrap',

                                                                    }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={filtroMarca === marca}
                                                                            onChange={() => {
                                                                                setFiltroMarca(marca);
                                                                                setCurrentPage(1);
                                                                            }}
                                                                        />
                                                                        {marca}
                                                                    </label>
                                                                ))}
                                                            </div>

                                                            {filtroMarca && (
                                                                <div className="mt-3">
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        onClick={() => {
                                                                            setFiltroMarca('');
                                                                            setCurrentPage(1);
                                                                        }}
                                                                    >
                                                                        Limpiar filtro
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </Col>
                                                    </div>
                                            )}
                                                                                    



                                        <hr style={{ width: '50%', margin: '0 auto', border: 'none', height: '2px', backgroundColor: '#000', marginTop: '0.5em', marginBottom: '1em' }} />
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <Button
                                                variant="outline-success"
                                                className="btn-descargar-stock px-2 py-2 text-center"
                                                onClick={() => generarExcel(articulosPorCategoria, setDescargado)}
                                                style={{ 
                                                    display: "flex", 
                                                    flexDirection: "column", 
                                                    alignItems: "center", 
                                                    opacity: showOnlyStock ? 1 : 0.5,
                                                    cursor: showOnlyStock ? "pointer" : "not-allowed",
                                                }}
                                                disabled={!showOnlyStock}
                                            >
                                                {showOnlyStock ? "Descargar planilla con Stock" : "Descargar planilla completa"}
                                                {/* <MdDownload size={25} style={{ marginTop: "4px" }} /> */}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Lista de Productos */}
                                <div style={{ flex: 4 }}>
                                    <Row className='mt-2' style={{ justifyContent: "center" }}>
                                        <Col xs={12} md={12} sm={6} xl={9} lg={12}>
                                            <ErrorBoundary>
                                                <div className="taleShadow bg-light">
                                                    {grid ? (
                                                        <CardCategorias articulosPorCategoria={articulosPorCategoria} />
                                                    ) : (
                                                        <TablesCategorias 
                                                            articulosPorCategoria={articulosPorCategoria} 
                                                            header={["", ""]} 
                                                        />
                                                    )}
                                                    
                                                    {/* Paginación */}
                                                    <Pagination className="justify-content-center mt-3">
                                                        <Pagination.Prev
                                                            disabled={currentPage === 1}
                                                            onClick={() => setCurrentPage(prev => prev - 1)}
                                                        />
                                                        {renderPagination()}
                                                        <Pagination.Next
                                                            disabled={currentPage === Math.ceil(largoTotal / elementosPorPagina)}
                                                            onClick={() => setCurrentPage(prev => prev + 1)}
                                                        />
                                                    </Pagination>

                                                    <div className="text-center mt-2">
                                                        Página {currentPage} de {Math.ceil(largoTotal / elementosPorPagina)}
                                                    </div>
                                                </div>
                                            </ErrorBoundary>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </Container>
                    ) : (
                        <div className="text-center py-5">
                            <p>{t("producto.no-existe-categoria")}</p>
                            <Button variant="primary" onClick={() => navigate('/')}>
                                {t("producto.volver-inicio")}
                            </Button>
                        </div>
                    )}
                </Suspense>
            </div>
        </ErrorBoundary>
    );
};

// Estilos
const styles = `
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
    .spin {
        animation: spin 1s linear infinite;
    }
`;

const styleElement = document.createElement('style');
styleElement.innerHTML = styles;
document.head.appendChild(styleElement);